/**
 * Gtag plugin: same event names and API as main project (frontendrealestate).
 * Uses generated event names from analytics/events.generated.ts.
 * If the host page (e.g. WordPress) already has gtag/dataLayer, events are pushed there.
 * Optional: pass gtagId in widget config to inject the GA script when not present.
 */
import { GA_EVENT, GA_CATEGORY, GTM_EVENT } from '../analytics/events.generated'

declare global {
  interface Window {
    dataLayer: unknown[]
    gtag: (...args: unknown[]) => void
  }
}

const BOST_FUNNEL_NAME = 'bost_acquisition'

/** Session cumulative funnel score for GA/GTM (widget embed). */
export const FUNNEL_SCORE_STORAGE_KEY = 'bost_acquisition_funnel_score'

function ensureDataLayer() {
  if (typeof window === 'undefined') return false
  window.dataLayer = window.dataLayer || []
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer.push(args)
    }
  }
  return true
}

/** Increment session funnel score; returns new total. */
export function bumpFunnelScore(scoreDelta: number): number {
  if (typeof window === 'undefined') return scoreDelta
  try {
    const prev =
      Number.parseInt(window.sessionStorage.getItem(FUNNEL_SCORE_STORAGE_KEY) || '0', 10) || 0
    const next = prev + scoreDelta
    window.sessionStorage.setItem(FUNNEL_SCORE_STORAGE_KEY, String(next))
    return next
  } catch {
    return scoreDelta
  }
}

export function readFunnelScore(): number {
  if (typeof window === 'undefined') return 0
  try {
    return Number.parseInt(window.sessionStorage.getItem(FUNNEL_SCORE_STORAGE_KEY) || '0', 10) || 0
  } catch {
    return 0
  }
}

function funnelBlock(step: number, scoreDelta: number, funnelScore: number): Record<string, unknown> {
  return {
    funnel_name: BOST_FUNNEL_NAME,
    funnel_step: step,
    score_delta: scoreDelta,
    funnel_score: funnelScore,
    page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
  }
}

function pushDataLayerWithFunnel(
  base: Record<string, unknown>,
  step: number,
  scoreDelta: number
): void {
  if (!ensureDataLayer() || !Array.isArray(window.dataLayer)) return
  const funnelScore = bumpFunnelScore(scoreDelta)
  window.dataLayer.push({ ...base, ...funnelBlock(step, scoreDelta, funnelScore) })
}

/** GTM dataLayer: contact modal opened (widget). */
export function pushLeadFormModalOpen(context?: { propertyId?: string }): void {
  if (!ensureDataLayer()) return
  try {
    const payload: Record<string, unknown> = {
      event: GTM_EVENT.LEAD_FORM_MODAL_OPEN,
    }
    if (context?.propertyId) payload.property_id = context.propertyId
    pushDataLayerWithFunnel(payload, 7, 40)
  } catch {
    // Analytics must never break UX
  }
}

/** First meaningful interaction per form field (dedupe in widget). */
export function pushBostFunnelLeadFieldInteraction(fieldName: string): void {
  if (!ensureDataLayer()) return
  try {
    pushDataLayerWithFunnel(
      {
        event: GTM_EVENT.BOST_FUNNEL_LEAD_FIELD_INTERACTION,
        field_name: fieldName,
      },
      8,
      15
    )
  } catch {
    // Analytics must never break UX
  }
}

/**
 * Initialize gtag script (optional). Call when widget is embedded without host GA.
 * Idempotent; safe to call with same measurementId multiple times.
 */
export function initGtag(measurementId: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return
  ensureDataLayer()
  window.gtag('js', new Date())
  if (!document.getElementById('ga-gtag')) {
    const script = document.createElement('script')
    script.id = 'ga-gtag'
    script.async = true
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
    document.head.appendChild(script)
  }
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    allow_google_signals: false,
    send_page_view: true,
  })
}

export type TrackEventFunnel =
  | { mode: 'increment'; step: number; scoreDelta: number }
  | { mode: 'attach'; step: number; scoreDelta: number; funnelScore: number }

/**
 * Track a generic GA event. Same signature as main project; optional funnel context
 * adds funnel_name, funnel_step, score_delta, funnel_score for GA4.
 */
export function trackEvent(
  action: string,
  category: string,
  label?: string,
  value?: number,
  funnel?: TrackEventFunnel
): void {
  if (!ensureDataLayer()) return
  try {
    const base: Record<string, unknown> = {
      event_category: category,
      event_label: label,
      value: value,
    }
    if (!funnel) {
      window.gtag('event', action, base)
      return
    }
    const page_path = typeof window !== 'undefined' ? window.location.pathname : undefined
    let funnel_score: number
    let score_delta: number
    let funnel_step: number
    if (funnel.mode === 'increment') {
      score_delta = funnel.scoreDelta
      funnel_step = funnel.step
      funnel_score = bumpFunnelScore(score_delta)
    } else {
      score_delta = funnel.scoreDelta
      funnel_step = funnel.step
      funnel_score = funnel.funnelScore
    }
    window.gtag('event', action, {
      ...base,
      funnel_name: BOST_FUNNEL_NAME,
      funnel_step,
      score_delta,
      funnel_score,
      page_path,
    })
  } catch {
    // Analytics must never break UX
  }
}

export type LeadFormTrackingContext = {
  propertyId?: string
}

export type LeadFormConversionContext = {
  conversationId?: string
  propertyId?: string
}

/**
 * Track lead form step submit (step 1 or 2). Same as main project.
 */
export function trackLeadFormStepSubmit(step: 1 | 2, context?: LeadFormTrackingContext): void {
  const labelParts: string[] = []
  if (context?.propertyId) {
    labelParts.push(`property:${context.propertyId}`)
  }
  const label = labelParts.length ? labelParts.join('|') : undefined
  const action = step === 1 ? GA_EVENT.LEAD_FORM_STEP_1_SUBMIT : GA_EVENT.LEAD_FORM_STEP_2_SUBMIT
  trackEvent(action, GA_CATEGORY.LEAD_FORM, label, undefined, {
    mode: 'increment',
    step: 8,
    scoreDelta: 0,
  })
}

/**
 * Track lead form conversion: push to dataLayer for GTM (e.g. Google Ads) and send GA event.
 * Same as main project.
 */
export function trackLeadFormConversion(context: LeadFormConversionContext): void {
  if (!ensureDataLayer()) return
  try {
    const funnelScore = bumpFunnelScore(300)
    const shared = funnelBlock(9, 300, funnelScore)

    const eventPayload: Record<string, unknown> = {
      event: GTM_EVENT.LEAD_FORM_SUBMIT,
      ...shared,
    }
    if (context.conversationId) {
      eventPayload.conversation_id = context.conversationId
    }
    if (context.propertyId) {
      eventPayload.property_id = context.propertyId
    }
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(eventPayload)
    }

    const funnelPayload: Record<string, unknown> = {
      event: GTM_EVENT.BOST_FUNNEL_LEAD_SUBMIT,
      ...shared,
    }
    if (context.conversationId) funnelPayload.conversation_id = context.conversationId
    if (context.propertyId) funnelPayload.property_id = context.propertyId
    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(funnelPayload)
    }

    if (context.conversationId) {
      trackEvent(GA_EVENT.LEAD_FORM_CONVERSION, GA_CATEGORY.LEAD_FORM, context.conversationId, undefined, {
        mode: 'attach',
        step: 9,
        scoreDelta: 300,
        funnelScore,
      })
    } else {
      trackEvent(GA_EVENT.LEAD_FORM_CONVERSION, GA_CATEGORY.LEAD_FORM, undefined, undefined, {
        mode: 'attach',
        step: 9,
        scoreDelta: 300,
        funnelScore,
      })
    }
  } catch {
    // Analytics must never break UX
  }
}

/**
 * Lead open — contact modal opened. Same as main project (lead_open / contact).
 */
export function trackLeadOpen(propertyIdOrShortRef?: string): void {
  trackEvent(GA_EVENT.LEAD_OPEN, GA_CATEGORY.CONTACT, propertyIdOrShortRef, undefined, {
    mode: 'increment',
    step: 7,
    scoreDelta: 0,
  })
}

/**
 * Lead submit attempt (user clicked send). Same as main project.
 */
export function trackLeadSubmitAttempt(propertyId?: string): void {
  trackEvent(GA_EVENT.LEAD_SUBMIT_ATTEMPT, GA_CATEGORY.CONTACT, propertyId, undefined, {
    mode: 'increment',
    step: 8,
    scoreDelta: 0,
  })
}

/**
 * Lead submit success. Same as main project.
 * No funnel_step here — step 9 / score_delta 300 live on dataLayer `lead_form_submit` /
 * `bost_funnel_lead_submit` and `lead_form_conversion` to avoid duplicate “step 9” in GA4.
 */
export function trackLeadSubmitSuccess(propertyId?: string): void {
  trackEvent(GA_EVENT.LEAD_SUBMIT_SUCCESS, GA_CATEGORY.CONTACT, propertyId)
}

/**
 * Lead submit error. Same as main project.
 */
export function trackLeadSubmitError(context?: { propertyId?: string; code?: string }): void {
  const parts = [
    context?.propertyId ? `prop=${context.propertyId}` : null,
    context?.code ? `code=${context.code}` : null,
  ].filter(Boolean)
  const label = parts.length ? parts.join(';') : undefined
  trackEvent(GA_EVENT.LEAD_SUBMIT_ERROR, GA_CATEGORY.CONTACT, label, undefined, {
    mode: 'increment',
    step: 8,
    scoreDelta: 0,
  })
}
